import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Clock,
  Edit3,
  Trash2,
  Plus,
  Users,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Filter,
  RefreshCw,
  UserX,
  Save,
  X,
  Download,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTimeManagement = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [filters, setFilters] = useState({
    staff_id: 'all',
    days: 30
  });

  // Form data for create/edit
  const [formData, setFormData] = useState({
    staff_id: '',
    staff_name: '',
    clock_in_time: '',
    clock_out_time: '',
    date: new Date().toISOString().split('T')[0],
    shift_type: 'Day',
    hours_worked: ''
  });

  // Bulk update data
  const [bulkData, setBulkData] = useState({
    clock_in_time: '',
    clock_out_time: '',
    shift_type: '',
    hours_worked: ''
  });

  useEffect(() => {
    fetchTimeEntries();
    fetchStaff();
  }, [filters]);

  const fetchTimeEntries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('days', filters.days);
      if (filters.staff_id && filters.staff_id !== 'all') params.append('staff_id', filters.staff_id);
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }
      
      const response = await axios.get(`${API}/admin/time-entries?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Error fetching time entries:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to load time entries';
      toast.error(errorMessage);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication expired. Please log in again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token available for staff fetch');
        return;
      }
      
      const response = await axios.get(`${API}/staff`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setStaff(response.data.filter(user => user.role !== 'admin'));
    } catch (error) {
      console.error('Error fetching staff:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error('Authentication error when fetching staff');
      }
    }
  };

  const handleCreateEntry = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }

      const payload = {
        ...formData,
        clock_in_time: new Date(formData.date + 'T' + formData.clock_in_time),
        clock_out_time: formData.clock_out_time ? new Date(formData.date + 'T' + formData.clock_out_time) : null,
        hours_worked: formData.hours_worked ? parseFloat(formData.hours_worked) : null
      };

      await axios.post(`${API}/admin/time-entries`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Time entry created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchTimeEntries();
    } catch (error) {
      console.error('Error creating time entry:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create time entry';
      toast.error(errorMessage);
    }
  };

  const handleUpdateEntry = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }

      const payload = {};
      if (formData.staff_id) payload.staff_id = formData.staff_id;
      if (formData.staff_name) payload.staff_name = formData.staff_name;
      if (formData.clock_in_time) payload.clock_in_time = new Date(formData.date + 'T' + formData.clock_in_time);
      if (formData.clock_out_time) payload.clock_out_time = new Date(formData.date + 'T' + formData.clock_out_time);
      if (formData.date) payload.date = formData.date;
      if (formData.shift_type) payload.shift_type = formData.shift_type;
      if (formData.hours_worked) payload.hours_worked = parseFloat(formData.hours_worked);

      await axios.put(`${API}/admin/time-entries/${selectedEntry.id}`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Time entry updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchTimeEntries();
    } catch (error) {
      console.error('Error updating time entry:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update time entry';
      toast.error(errorMessage);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    
    try {
      // Ensure we have the auth token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }

      const response = await axios.delete(`${API}/admin/time-entries/${entryId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Delete response:', response.data);
      toast.success('Time entry deleted successfully');
      fetchTimeEntries();
    } catch (error) {
      console.error('Error deleting time entry:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete time entry';
      toast.error(`Delete failed: ${errorMessage}`);
      
      // If it's an auth error, suggest re-login
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication expired. Please log in again.');
      }
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedEntries.length === 0) {
      toast.error('No entries selected');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }

      const updates = {};
      if (bulkData.clock_in_time) updates.clock_in_time = new Date(bulkData.clock_in_time);
      if (bulkData.clock_out_time) updates.clock_out_time = new Date(bulkData.clock_out_time);
      if (bulkData.shift_type && bulkData.shift_type !== 'no-change') updates.shift_type = bulkData.shift_type;
      if (bulkData.hours_worked) updates.hours_worked = parseFloat(bulkData.hours_worked);

      await axios.put(`${API}/admin/time-entries/bulk-update`, {
        entry_ids: selectedEntries,
        updates: updates
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success(`Updated ${selectedEntries.length} time entries`);
      setShowBulkDialog(false);
      setSelectedEntries([]);
      setBulkData({});
      fetchTimeEntries();
    } catch (error) {
      console.error('Error bulk updating entries:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to bulk update entries';
      toast.error(errorMessage);
    }
  };

  const handleForceClockOut = async (staffId, staffName) => {
    if (!window.confirm(`Force clock out ${staffName}? This will end their current shift.`)) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }

      await axios.post(`${API}/admin/force-clock-out/${staffId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(`${staffName} has been clocked out`);
      fetchTimeEntries();
    } catch (error) {
      console.error('Error force clocking out:', error);
      const message = error.response?.data?.detail || 'Failed to clock out staff';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setFormData({
      staff_id: '',
      staff_name: '',
      clock_in_time: '',
      clock_out_time: '',
      date: new Date().toISOString().split('T')[0],
      shift_type: 'Day',
      hours_worked: ''
    });
    setSelectedEntry(null);
  };

  const editEntry = (entry) => {
    setSelectedEntry(entry);
    
    const clockInTime = new Date(entry.clock_in_time);
    const clockOutTime = entry.clock_out_time ? new Date(entry.clock_out_time) : null;
    
    setFormData({
      staff_id: entry.staff_id,
      staff_name: entry.staff_name,
      clock_in_time: clockInTime.toTimeString().slice(0, 5),
      clock_out_time: clockOutTime ? clockOutTime.toTimeString().slice(0, 5) : '',
      date: entry.date,
      shift_type: entry.shift_type || 'Day',
      hours_worked: entry.hours_worked?.toString() || ''
    });
    setShowEditDialog(true);
  };

  const toggleEntrySelection = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const formatDateTime = (datetime) => {
    return new Date(datetime).toLocaleString();
  };

  const getStatusBadge = (entry) => {
    if (!entry.clock_out_time) {
      return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Still Clocked In</Badge>;
    }
    return <Badge variant="outline" className="bg-green-100 text-green-800">Completed</Badge>;
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <Clock className="h-8 w-8 text-indigo-600" />
            <span>Admin Time Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Manage staff time entries, corrections, and clock-out issues</p>
        </div>
        
        <div className="flex space-x-3 mt-4 sm:mt-0">
          {selectedEntries.length > 0 && (
            <Button
              onClick={() => setShowBulkDialog(true)}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <Edit3 className="h-4 w-4" />
              <span>Bulk Edit ({selectedEntries.length})</span>
            </Button>
          )}
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Manual Entry</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Staff Member</label>
              <Select value={filters.staff_id} onValueChange={(value) => setFilters({...filters, staff_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Date Range</label>
              <Select value={filters.days.toString()} onValueChange={(value) => setFilters({...filters, days: parseInt(value)})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button onClick={fetchTimeEntries} className="flex items-center space-x-2">
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Time Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Time Entries ({timeEntries.length})</CardTitle>
          <CardDescription>
            Manage all staff time entries with full admin control
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          ) : timeEntries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No time entries found for the selected criteria
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">
                      <input
                        type="checkbox"
                        checked={selectedEntries.length === timeEntries.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEntries(timeEntries.map(entry => entry.id));
                          } else {
                            setSelectedEntries([]);
                          }
                        }}
                      />
                    </th>
                    <th className="text-left p-2">Staff</th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Clock In</th>
                    <th className="text-left p-2">Clock Out</th>
                    <th className="text-left p-2">Hours</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timeEntries.map((entry) => (
                    <tr key={entry.id} className="border-b hover:bg-gray-50">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedEntries.includes(entry.id)}
                          onChange={() => toggleEntrySelection(entry.id)}
                        />
                      </td>
                      <td className="p-2">{entry.staff_name}</td>
                      <td className="p-2">{entry.date}</td>
                      <td className="p-2">{formatDateTime(entry.clock_in_time)}</td>
                      <td className="p-2">
                        {entry.clock_out_time ? formatDateTime(entry.clock_out_time) : 
                         <span className="text-yellow-600">Still clocked in</span>}
                      </td>
                      <td className="p-2">{entry.hours_worked?.toFixed(2) || '0.00'} hrs</td>
                      <td className="p-2">{getStatusBadge(entry)}</td>
                      <td className="p-2">
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => editEntry(entry)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {!entry.clock_out_time && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleForceClockOut(entry.staff_id, entry.staff_name)}
                              className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700"
                            >
                              <UserX className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Time Entry Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Manual Time Entry</DialogTitle>
            <DialogDescription>
              Add a time entry for staff who forgot to clock in/out
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Staff Member *</label>
              <Select value={formData.staff_id} onValueChange={(value) => {
                const selectedStaff = staff.find(s => s.id === value);
                setFormData({...formData, staff_id: value, staff_name: selectedStaff?.full_name || ''});
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Date *</label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Clock In Time *</label>
                <Input
                  type="time"
                  value={formData.clock_in_time}
                  onChange={(e) => setFormData({...formData, clock_in_time: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Clock Out Time</label>
                <Input
                  type="time"
                  value={formData.clock_out_time}
                  onChange={(e) => setFormData({...formData, clock_out_time: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Shift Type</label>
                <Select value={formData.shift_type} onValueChange={(value) => setFormData({...formData, shift_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Day">Day</SelectItem>
                    <SelectItem value="Evening">Evening</SelectItem>
                    <SelectItem value="Night">Night</SelectItem>
                    <SelectItem value="Weekend">Weekend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Hours Worked</label>
                <Input
                  type="number"
                  step="0.25"
                  value={formData.hours_worked}
                  onChange={(e) => setFormData({...formData, hours_worked: e.target.value})}
                  placeholder="Auto-calculated"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => {setShowCreateDialog(false); resetForm();}}>
                Cancel
              </Button>
              <Button onClick={handleCreateEntry} className="bg-indigo-600 hover:bg-indigo-700">
                <Save className="h-4 w-4 mr-2" />
                Create Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Time Entry Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
            <DialogDescription>
              Modify the time entry details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Staff Member</label>
              <Input value={formData.staff_name} disabled />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Date</label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Clock In Time</label>
                <Input
                  type="time"
                  value={formData.clock_in_time}
                  onChange={(e) => setFormData({...formData, clock_in_time: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Clock Out Time</label>
                <Input
                  type="time"
                  value={formData.clock_out_time}
                  onChange={(e) => setFormData({...formData, clock_out_time: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Shift Type</label>
                <Select value={formData.shift_type} onValueChange={(value) => setFormData({...formData, shift_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Day">Day</SelectItem>
                    <SelectItem value="Evening">Evening</SelectItem>
                    <SelectItem value="Night">Night</SelectItem>
                    <SelectItem value="Weekend">Weekend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Hours Worked</label>
                <Input
                  type="number"
                  step="0.25"
                  value={formData.hours_worked}
                  onChange={(e) => setFormData({...formData, hours_worked: e.target.value})}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => {setShowEditDialog(false); resetForm();}}>
                Cancel
              </Button>
              <Button onClick={handleUpdateEntry} className="bg-orange-600 hover:bg-orange-700">
                <Save className="h-4 w-4 mr-2" />
                Update Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Update Time Entries</DialogTitle>
            <DialogDescription>
              Update {selectedEntries.length} selected entries
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Clock In Time</label>
                <Input
                  type="time"
                  value={bulkData.clock_in_time}
                  onChange={(e) => setBulkData({...bulkData, clock_in_time: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Clock Out Time</label>
                <Input
                  type="time"
                  value={bulkData.clock_out_time}
                  onChange={(e) => setBulkData({...bulkData, clock_out_time: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Shift Type</label>
                <Select value={bulkData.shift_type} onValueChange={(value) => setBulkData({...bulkData, shift_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-change">No change</SelectItem>
                    <SelectItem value="Day">Day</SelectItem>
                    <SelectItem value="Evening">Evening</SelectItem>
                    <SelectItem value="Night">Night</SelectItem>
                    <SelectItem value="Weekend">Weekend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Hours Worked</label>
                <Input
                  type="number"
                  step="0.25"
                  value={bulkData.hours_worked}
                  onChange={(e) => setBulkData({...bulkData, hours_worked: e.target.value})}
                  placeholder="No change"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => {setShowBulkDialog(false); setBulkData({});}}>
                Cancel
              </Button>
              <Button onClick={handleBulkUpdate} className="bg-purple-600 hover:bg-purple-700">
                <Save className="h-4 w-4 mr-2" />
                Update All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTimeManagement;